import { Palette } from '@mui/material';
import {
	abmStyles,
	aceStyles,
	atlanticStyles,
	citrinStyles,
	cityParkingStyles,
	iconStyles,
	ipmStyles,
	lasVegasParkingStyles,
	legacyStyles,
	littlemanStyles,
	makeParkingPaymentStyles,
	nationalParkingStyles,
	oneParkingStyles,
	ovgStyles,
	parkchirpDarkStyles,
	parkchirpStyles,
	parkingAdvantageStyles,
	parkingCompanyOfAmericaStyles,
	parkingNwStyles,
	parkingSystemsOfAmericaStyles,
	parkwellStyles,
	parkXStyles,
	pcamStyles,
	pciStyles,
	peakStyles,
	pivotStyles,
	platinumStyles,
	primeStyles,
	proparkStyles,
	rezyParkStyles,
	signatureParkingStyles,
	truparkStyles,
	unitedParkingSystemsStyles,
	universalStyles,
} from '../constants';
import { OrganizationThemeEnum } from '../enums';
import { ThemeStyles } from '../types';

export function getOrgThemeName(org?: string) {
	try {
		if (!org) return OrganizationThemeEnum.DEFAULT;

		const normalizeOrg = org.toLocaleLowerCase();

		const themeName = Object.values(OrganizationThemeEnum).find((o) => o === normalizeOrg);

		return themeName || OrganizationThemeEnum.DEFAULT;
	} catch {
		return OrganizationThemeEnum.DEFAULT;
	}
}

export function getThemeOverrides(org?: string) {
	if (![OrganizationThemeEnum.DEFAULT, OrganizationThemeEnum.DEFAULT_DARK].includes(org as OrganizationThemeEnum)) {
		return {};
	}

	let overrides = {};

	try {
		const definition = localStorage?.getItem('parkchirp-overrides');
		if (definition) {
			overrides = JSON.parse(definition);
		}
	} catch {}

	if (org === OrganizationThemeEnum.DEFAULT_DARK) {
		const styleId = 'parkchirp-dark';
		let style = document.getElementById(styleId);

		if (!style) {
			style = document.createElement('style');
			style.id = styleId;

			style.textContent = `
				:root {
					color-scheme: dark;
				}

				html, body {
					color: white;
				}

				a.tabNavSelection {
					color: white !important;
				}

				a:not(:has(svg)) {
					color: #5cc1ff !important;
				}

				.editor-modal-content .editor-modal-header-text {
					color: white;
				}

				.pca__dashboard--searchbox {
					border: 1px solid #f8f8f828;
					background-color: #f8f8f810;
				}
			`;

			document.head.appendChild(style);
		}
	}

	switch (process.env.NEXT_ENVIRONMENT as string) {
		case 'DEVELOPMENT':
		case 'QA':
			return {
				headerGradient: {
					background: 'linear-gradient(90deg, #2a2f3c 0%, #00c94b 100%);',
				},
				...overrides,
			};
		case 'STG':
			return {
				headerGradient: {
					background: 'linear-gradient(90deg, #2a2f3c 0%, #00aeef 100%);',
				},
				...overrides,
			};
		default:
			return { ...overrides };
	}
}

export function getThemeStyles(org?: string) {
	let orgName = org;

	try {
		if (org === OrganizationThemeEnum.DEFAULT) {
			orgName = localStorage?.getItem('parkchirp-theme') ?? OrganizationThemeEnum.DEFAULT;
		}
	} catch {}

	const orgTheme = getOrgThemeName(orgName);

	const themeStyles: ThemeStyles = {
		[OrganizationThemeEnum.DEFAULT]: parkchirpStyles,
		[OrganizationThemeEnum.DEFAULT_DARK]: parkchirpDarkStyles,
		[OrganizationThemeEnum.ABM]: abmStyles,
		[OrganizationThemeEnum.UNIVERSAL]: universalStyles,
		[OrganizationThemeEnum.PCI]: pciStyles,
		[OrganizationThemeEnum.LITTLE_MAN_PARKING]: littlemanStyles,
		[OrganizationThemeEnum.PROPARK]: proparkStyles,
		[OrganizationThemeEnum.LEGACY]: legacyStyles,
		[OrganizationThemeEnum.PRIME]: primeStyles,
		[OrganizationThemeEnum.ATLANTIC]: atlanticStyles,
		[OrganizationThemeEnum.NATIONAL_PARKING]: nationalParkingStyles,
		[OrganizationThemeEnum.PEAK_PARKING]: peakStyles,
		[OrganizationThemeEnum.PARKWELL]: parkwellStyles,
		[OrganizationThemeEnum.PARKING_NW]: parkingNwStyles,
		[OrganizationThemeEnum.IPM]: ipmStyles,
		[OrganizationThemeEnum.ACE]: aceStyles,
		[OrganizationThemeEnum.PIVOT_PARKING]: pivotStyles,
		[OrganizationThemeEnum.ONE_PARKING]: oneParkingStyles,
		[OrganizationThemeEnum.PARKING_SYSTEMS_OF_AMERICA]: parkingSystemsOfAmericaStyles,
		[OrganizationThemeEnum.CITRIN]: citrinStyles,
		[OrganizationThemeEnum.OVG]: ovgStyles,
		[OrganizationThemeEnum.PLATINUM]: platinumStyles,
		[OrganizationThemeEnum.ICON]: iconStyles,
		[OrganizationThemeEnum.PARKX]: parkXStyles,
		[OrganizationThemeEnum.PARKING_COMPANY_OF_AMERICA]: parkingCompanyOfAmericaStyles,
		[OrganizationThemeEnum.PARKING_ADVANTAGE]: parkingAdvantageStyles,
		[OrganizationThemeEnum.MAKE_PARKING_PAYMENT]: makeParkingPaymentStyles,
		[OrganizationThemeEnum.LAS_VEGAS_PARKING]: lasVegasParkingStyles,
		[OrganizationThemeEnum.CITY_PARKING]: cityParkingStyles,
		[OrganizationThemeEnum.PCAM]: pcamStyles,
		[OrganizationThemeEnum.TRUPARK]: truparkStyles,
		[OrganizationThemeEnum.SIGNATURE_PARKING]: signatureParkingStyles,
		[OrganizationThemeEnum.REZY_PARK]: rezyParkStyles,
		[OrganizationThemeEnum.UNITED_PARKING_SYSTEMS]: unitedParkingSystemsStyles,
	};

	const themeValues = themeStyles[orgTheme] || themeStyles[OrganizationThemeEnum.DEFAULT]!;
	const themeOverrides = getThemeOverrides(orgName);

	return {
		...themeValues,
		...themeOverrides,
	};
}

export const getOrgColorTheme = (orgName?: string, appName?: string) => {
	const {
		mode = 'light',
		density = 'standard',
		background = {},
		header,
		headerGradient = {},
		sidebar,
		button,
		icon = {},
		text,
		table,
		dataGrid = {},
		dataGridToolbar = {},
		paper = {},
		title,
		logo,
		platformName,
		showLargeMenuIconButton = false,
		typography,
		socials,
		orgName: readableOrgName,
		siteUrl,
		favicon,
	} = getThemeStyles(orgName) as any;
	const fontFamily =
		appName === 'MONTHLY_CHECKOUT'
			? ['FilsonSoft-Bold', 'FilsonSoft-Heavy', 'FilsonSoft-Light', 'sans-serif'].join(',')
			: typography?.fontFamily || 'inherit';

	return {
		header,
		sidebar,
		button,
		text,
		table,
		title,
		logo,
		platformName,
		socials,
		siteUrl,
		readableOrgName,
		favicon,
		palette: {
			mode,
			background,
			primary: {
				main: text.color,
			},
			secondary: {
				main: sidebar.backgroundColor,
			},
		} as Palette,
		components: {
			MuiInputLabel: {
				styleOverrides: {
					root: {
						fontFamily,
						color: text.color,
					},
				},
			},
			MuiCard: {
				styleOverrides: {
					root: {
						backgroundColor: '#f8f8f8',
						border: '1px solid rgba(224, 224, 224, 1)',
						boxShadow: 'initial',
						...paper,
					},
				},
			},
			MuiTableContainer: {
				styleOverrides: {
					root: {
						...table,
					},
				},
			},
			MuiAccordion: {
				styleOverrides: {
					root: {
						fontFamily,
					},
				},
			},
			MuiAccordionSummary: {
				styleOverrides: {
					root: {
						fontFamily,
					},
				},
			},
			MuiAccordionDetails: {
				styleOverrides: {
					root: {
						fontFamily,
					},
				},
			},
			MuiAlert: {
				styleOverrides: {
					root: {
						color: 'white',
					},
				},
			},
			MuiOutlinedInput: {
				styleOverrides: {
					root: {
						fontFamily,
						color: text.color,
					},
				},
			},
			MuiSelect: {
				styleOverrides: {
					root: {
						fontFamily,
						color: text.color,
					},
				},
			},
			MuiDialog: {
				styleOverrides: {
					root: {
						'& .parker-detail .MuiDataGrid-columnHeaders': {
							backgroundColor: table.headerBackgroundColor,
							color: table.headerTextColor,
						},
					},
					paper,
				},
			},
			MuiSvgIcon: {
				styleOverrides: {
					root: {
						'.MuiDataGrid-panel &[data-testid="CloseIcon"]': {
							color: 'black',
						},
					},
				},
			},
			MuiButton: {
				styleOverrides: {
					root: {
						fontFamily,
						padding: '6px 8px',
						margin: '0px 5px 0px 0px',
						...button,
						'&.Mui-disabled': { ...button, opacity: 0.5 },
						'& .MuiCircularProgress-svg': {
							color: text.buttonLoadingColor || text.color,
						},
						'&:hover': {
							backgroundColor: button[':hover'].backgroundColor ?? button.backgroundColor,
						},
						'&.inputButtons': {
							backgroundColor: 'transparent',
						},
						'&.inputButtons:hover': {
							backgroundColor: 'inherit',
						},
						'&.transparent-button:hover': {
							boxShadow: 'none',
							backgroundColor: 'transparent',
						},
					},
					outlined: {
						backgroundColor: '#00000000',
						color: text.color,
						'&:hover': {
							color: text.color,
							backgroundColor: '#00000000',
						},
						'&.Mui-disabled': {
							color: text.color,
							backgroundColor: '#00000000',
							opacity: 0.5,
						},
					},
				},
			},
			MuiContainer: {
				styleOverrides: {
					root: {
						// Allows us to switch between large and small logos
						'& #pca__sidebar--large-logo': {
							display: 'none',
						},
						'&#pca__sidebar&:hover': {
							width: '210px',
							'& #pca__sidebar--large-logo': {
								display: 'block',
							},
							'& #pca__sidebar--small-logo': {
								display: 'none',
							},
						},
						'& #pca__sidebar-logo': {
							paddingTop: '10px',
						},
						'&#pca__sidebar': sidebar,
					},
				},
			},
			MuiHeaderContainer: {
				styleOverrides: {
					root: {
						display: 'flex',
						justifyContent: 'space-between',
						'&#pca__header': {
							...header,
							...headerGradient,
							height: '50px',
						},
						'&#pca__detail_header': {
							...header,
						},
						'& #pca__header-center--title': {
							paddingTop: '4px',
							paddingBottom: '2px',
						},
						'& #pca__header-center--title h1, h4, h2, p, .header-sign-out, .tenant-profile': {
							color: title?.color || text.color,
							textTransform: 'capitalize',
						},
					},
				},
			},
			MuiTab: {
				styleOverrides: {
					root: {
						'&.Mui-selected': {
							borderRadius: '4px',
							backgroundColor: sidebar.backgroundColor,
							color: '#ffffff',
						},
					},
				},
			},
			MuiTypography: {
				styleOverrides: {
					root: {
						color: text.color,
						fontFamily,
						// fontWeight: 'normal',
						// fontSize: '1rem',
						// lineHeight: '1.5',
					},
				},
			},
			MuiTableCell: {
				styleOverrides: {
					root: {
						color: text.color,
						padding: '15px',
					},
				},
			},
			MuiTableHead: {
				styleOverrides: {
					root: {
						backgroundColor: table.backgroundColor ?? 'white',
						color: 'black',
					},
				},
			},
			MuiTableRow: {
				styleOverrides: {
					root: {
						'&.pca__table--header .MuiTableCell-root': {
							color: table.headerTextColor,
							backgroundColor: table.headerBackgroundColor,
						},
					},
				},
			},
			MuiCircularProgress: {
				styleOverrides: {
					root: {
						'&.history-balance-loading svg': {
							color: text.color,
						},
					},
					svg: {
						color: text.color,
					},
				},
			},
			MuiBackdrop: {
				styleOverrides: {
					root: {
						zIndex: 9,
					},
				},
			},
			MuiIconButton: {
				styleOverrides: {
					root: {
						color: orgName === 'parkchirp' || orgName === 'ParkX' ? 'black' : title?.color,
						'& .MuiIconButton-sizeSmall': {
							color: 'black !important',
						},
						'& .MuiIconButton-sizeMedium .MuiIconButton-edgeEnd': {
							color: 'black !important',
						},
						...icon,
					},
				},
			},
			MuiDataGrid: {
				defaultProps: {
					density,
				},
				styleOverrides: {
					root: {
						'& .MuiDataGrid-toolbarContainer': {
							backgroundColor: '#f8f8f8',
							padding: '6px',
							borderRadius: '3px 3px 0 0',
							borderBottom: '1px solid rgba(224, 224, 224, 1)',
							gap: '0px',
							...dataGridToolbar,
						},
						'& .MuiIconButton-root': {
							color: 'black',
							...icon,
						},
						marginTop: '10px',
						height: '88vh',
						'& .MuiDataGrid-filterFormDeleteIcon': {
							color: 'black !important',
						},
						...(showLargeMenuIconButton && {
							'& .MuiDataGrid-menuIconButton': {
								width: '100px',
								height: '50px',
								borderRadius: '50%',
							},
						}),
						'& .MuiDataGrid-columnHeaderTitleContainerContent .MuiButton-root': {
							padding: '3px 8px !important',
						},
						'& .MuiDataGrid-cell .MuiButton-root': {
							padding: '3px 8px !important',
						},
						...dataGrid,
					},
				},
			},
			MuiInputAdornment: {
				styleOverrides: {
					root: {
						fontFamily,
						color: text.color,
						marginRight: '12px',
					},
				},
			},
			MuiMenuItem: {
				styleOverrides: {
					root: {
						fontFamily,
					},
				},
			},
			MuiToggleButton: {
				styleOverrides: {
					root: {
						fontFamily,
					},
				},
			},
			MuiButtonBase: {
				styleOverrides: {
					root: {
						'&.MuiIconButton-sizeMedium': {
							color: 'black !important', // Specifically for medium-sized buttons
						},
						...icon,
					},
				},
			},
			MuiDatePicker: {
				styleOverrides: {
					root: {
						'& .MuiInputBase-input': {
							fontFamily,
							color: text.color,
						},
						'& .MuiInputLabel-root': {
							fontFamily,
							color: text.color,
						},
						'& .MuiPickersDay-root': {
							fontFamily,
						},
						'& .MuiPickersDay-today': {
							borderColor: sidebar.backgroundColor,
							'&.Mui-selected': {
								backgroundColor: button.backgroundColor,
								color: '#ffffff',
							},
						},
						'& .MuiPickersDay-selected': {
							backgroundColor: button.backgroundColor,
							color: '#ffffff',
							'&:hover': {
								backgroundColor: button.backgroundColor,
								opacity: 0.8,
							},
						},
						'& .MuiPickersCalendarHeader-switchViewButton': {
							fontFamily,
							color: text.color,
						},
					},
				},
			},
		},
	};
};

export const siteMap: Record<string, string> = {
	parkchirp: 'parkchirp',
	chirpqa: 'parkchirp',
	parkcheep: 'parkchirp',
	legacyparking: 'legacy',
	abmparking: 'abm',
	asgpark: 'atlantic',
	ipmseattle: 'ipm',
	goparkwell: 'parkwell',
	propark: 'propark',
	parkingconcepts: 'pci',
	parkwithprime: 'prime',
	littlemanparking: 'little man parking',
	peakparking: 'peak parking',
	parkingnw: 'parking nw',
	universalparking: 'universal',
	parkingadvantage: 'parking advantage',
	parkwithace: 'ace',
	platinumparking: 'platinum',
	'universal.parkchirp': 'universal',
	'platinum.parkchirp': 'platinum',
	'national-parking': 'national parking',
	parkingsystemsofamerica: 'parking systems of america',
	pivotparking: 'pivot parking',
	oneparking: 'one parking',
	citrin: 'citrin',
	parkovg: 'oak view group',
	iconparking: 'icon',
	parkxmanagement: 'parkX',
	parkwithpca: 'parking company of america',
	makeparkingpayment: 'make parking payment',
	lvparking: 'las vegas parking',
	cityparkingonline: 'cityparking inc.',
	parkpca: 'pcam',
	truparkusa: 'trupark',
	signatureparkingtn: 'signature parking',
	rezypark: 'rezy park',
	unitedparkingsystem: 'united parking systems',
};
